import {
	storefrontProductPath,
	type PortalStorefrontItemCardDto,
} from '@supply-mover-2-0/shared';
import { ItemCarousel as SharedItemCarousel } from '@supply-mover-2-0/web-ui';
import { useQueryClient } from '@tanstack/react-query';
import type { JSX, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../../../hooks/useDisplaySettings';
import { StorefrontCardCartControl } from './StorefrontItemCard';

/**
 * The storefront's item row: the shared carousel (libs/web-ui) wired to the
 * storefront's router, cart control, pricing and item-image visibility, and
 * image self-heal.
 * Used by the Reorder, Popular, items-by-category and promo-with-items
 * widgets.
 */
export function ItemCarousel({
	items,
	emptyState,
	invalidateQueryKey,
	density = 'standard',
}: {
	items: Array<PortalStorefrontItemCardDto>;
	emptyState: ReactNode;
	/**
	 * Query key whose payload produced these items' signed image links. On a
	 * stale-image failure it is invalidated so the caller refetches fresh
	 * links. Optional for surfaces without a single owning query.
	 */
	invalidateQueryKey?: ReadonlyArray<unknown>;
	density?: 'standard' | 'compact';
}): JSX.Element {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const displaySettings = useDisplaySettings();
	return (
		<SharedItemCarousel
			items={items}
			emptyState={emptyState}
			density={density}
			showPrices={displaySettings.canSeeCatalogPricing}
			// PD-17144: brands without real product photos hide item images.
			showImages={displaySettings.showItemImages}
			onSelectItem={(item) => {
				void navigate(storefrontProductPath(item.id));
			}}
			// PD-17174 — real product links, for new tabs and search engines.
			itemHref={(item) => storefrontProductPath(item.id)}
			renderCardActions={(item) => <StorefrontCardCartControl item={item} />}
			onLikelyStaleUrl={
				invalidateQueryKey != null
					? (): void => {
							void queryClient.invalidateQueries({
								queryKey: invalidateQueryKey,
							});
						}
					: undefined
			}
		/>
	);
}
